/**
 * useInvertedColor Hook
 * Inverts any color token to its opposite value for high contrast scenarios
 */

import type {
  TextColorToken,
  SurfaceColorToken,
  BorderColorToken,
  InteractiveColorToken,
  InvertedColorToken,
} from "../foundation/Color";

// Internal factory function to create inverted color tokens
const createInvertedColorToken = (originalToken: TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken): InvertedColorToken => {
  if (!originalToken || typeof originalToken !== "object") {
    throw new Error("useInvertedColor: originalToken must be a valid color token object");
  }

  return {
    _originalToken: originalToken,
    _semantic: "inverted",
  } as InvertedColorToken;
};

/**
 * Hook that inverts any color token by swapping its light/dark mode values
 *
 * @param colorToken - Any CDS color token to invert
 * @returns An InvertedColorToken that uses dark mode colors in light mode and vice versa
 *
 * @example
 * // If Color.text.primary is dark in light mode, white in dark mode:
 * // invertedText will be white in light mode, dark in dark mode
 * const invertedText = useInvertedColor(Color.text.primary);
 *
 * // Use directly in components
 * <CDS.Text color={invertedText}>Inverted color mode text</CDS.Text>
 *
 * // Works with any color token
 * const invertedSurface = useInvertedColor(Color.surface.primary);
 * const invertedBorder = useInvertedColor(Color.border.primary);
 */
export function useInvertedColor(colorToken: TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken): InvertedColorToken {
  // Add runtime validation
  if (!colorToken) {
    throw new Error("useInvertedColor: colorToken is required");
  }

  // Return an inverted color token that will be handled by useColorModeValue
  return createInvertedColorToken(colorToken);
}

/**
 * Static helper function to get inverted color token (non-hook version)
 * Useful in contexts where hooks can't be used
 *
 * @param colorToken - Any CDS color token to invert
 * @returns An InvertedColorToken
 */
export function getInvertedColorToken(colorToken: TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken): InvertedColorToken {
  return createInvertedColorToken(colorToken);
}
